import { useRef } from 'react'
import { formatDate } from '@/lib/format'
import { useWedding } from '@/mock/store'
import { DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { ceremonyKindLabels, ceremonyPerson, ceremonySummary } from './model'
import { printCeremonySummary } from './print-summary'

export function CeremonySummarySheet({ onClose }: { onClose: () => void }) {
  const data = useWedding()
  const { event, venue, officiant, brideWitness, groomWitness, planBDecider } =
    ceremonySummary(data)
  const { ceremony } = data
  const { showToast } = useToast()
  const content = useRef<HTMLDivElement>(null)
  const sectionTitle = 'border-b border-border pb-2 font-heading text-xl'
  return (
    <DetailSheet
      open
      width="wide"
      eyebrow="Podklady"
      title="Souhrn obřadu"
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      primaryAction={{
        label: 'Tisk / uložit PDF',
        onClick: () => {
          if (content.current)
            printCeremonySummary(content.current, () =>
              showToast({
                title: 'Tisk se nepodařilo otevřít',
                description: 'Zkuste tisk otevřít znovu.',
                tone: 'danger',
              }),
            )
        },
      }}
    >
      <div ref={content} className="space-y-7 text-sm leading-6 break-words">
        <header>
          <h1 className="font-heading text-3xl">
            {[data.identity.brideName, data.identity.groomName].filter(Boolean).join(' a ') ||
              'Váš obřad'}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {ceremony.kind ? ceremonyKindLabels[ceremony.kind] : 'Podoba obřadu zatím otevřená'}
          </p>
        </header>
        <section className="space-y-4">
          <h2 className={sectionTitle}>Kdy a kde</h2>
          <dl className="summary-grid grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-muted-foreground">Datum</dt>
              <dd className="font-semibold">
                {data.identity.date ? formatDate(data.identity.date, 'long') : 'Zatím neurčené'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Čas</dt>
              <dd className="font-semibold">
                {event?.time || 'Zatím neurčený'}
                {event?.endTime
                  ? ` – ${event.endTime}${event.time && event.endTime < event.time ? ' (následující den)' : ''}`
                  : ''}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Místo</dt>
              <dd className="break-words font-semibold">{venue?.name || 'Zatím nevybrané'}</dd>
              {event?.location ? <dd className="break-words">{event.location}</dd> : null}
            </div>
          </dl>
        </section>
        <section className="space-y-4">
          <h2 className={sectionTitle}>Lidé a role</h2>
          <dl className="summary-grid grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-muted-foreground">Oddávající</dt>
              <dd className="break-words font-semibold">{officiant?.name || 'Zatím nevybraný'}</dd>
              {ceremony.officiantArrival ? <dd>Příjezd {ceremony.officiantArrival}</dd> : null}
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Svědci</dt>
              <dd className="break-words">
                {data.identity.brideName || 'První z páru'}:{' '}
                {brideWitness?.name || 'zatím nevybraný'}
              </dd>
              <dd className="break-words">
                {data.identity.groomName || 'Druhý z páru'}:{' '}
                {groomWitness?.name || 'zatím nevybraný'}
              </dd>
            </div>
          </dl>
          {ceremony.roles.length ? (
            <dl className="space-y-3">
              {ceremony.roles.map((role) => (
                <div key={role.id} className="summary-item">
                  <dt className="font-semibold">{role.title}</dt>
                  <dd>{ceremonyPerson(role.person, data)?.name || 'Zatím nepřiřazeno'}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </section>
        <section className="space-y-4">
          <h2 className={sectionTitle}>Scénář obřadu</h2>
          {ceremony.steps.length ? (
            <ol className="list-decimal space-y-4 pl-5 marker:text-muted-foreground">
              {ceremony.steps.map((step) => (
                <li key={step.id} className="summary-item pl-1">
                  <h3 className="font-semibold">
                    {step.title}
                    {step.durationMinutes ? ` · ${step.durationMinutes} min` : ''}
                  </h3>
                  {step.people.length ? (
                    <p className="text-xs text-muted-foreground">
                      {step.people
                        .map(
                          (person) =>
                            ceremonyPerson(person, data)?.name || 'Dříve přiřazený člověk',
                        )
                        .join(' · ')}
                    </p>
                  ) : null}
                  {step.description ? (
                    <p className="summary-note break-words whitespace-pre-line">
                      {step.description}
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-muted-foreground">Scénář zatím není sestavený.</p>
          )}
        </section>
        <section className="space-y-4">
          <h2 className={sectionTitle}>Plán B</h2>
          {ceremony.planB ? (
            <dl className="summary-grid grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-muted-foreground">Náhradní místo</dt>
                <dd className="break-words font-semibold">
                  {ceremony.planB.location || 'Zatím nevybrané'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Rozhodnutí o přesunu</dt>
                <dd>{planBDecider?.name || 'Člověk zatím neurčený'}</dd>
                <dd>
                  {ceremony.planB.decisionTime
                    ? `V ${ceremony.planB.decisionTime}`
                    : 'Čas zatím neurčený'}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-muted-foreground">Náhradní varianta zatím není domluvená.</p>
          )}
        </section>
        {ceremony.preparations.length ? (
          <section className="space-y-4">
            <h2 className={sectionTitle}>Příprava místa</h2>
            {ceremony.preparations.map((item) => (
              <div key={item.id} className="summary-item space-y-1">
                <h3 className="font-semibold">{item.title}</h3>
                <p className="text-xs text-muted-foreground">
                  {ceremonyPerson(item.person, data)?.name || 'Zatím nepřiřazeno'}
                </p>
                {item.description ? (
                  <p className="summary-note break-words whitespace-pre-line">{item.description}</p>
                ) : null}
              </div>
            ))}
          </section>
        ) : null}
        {ceremony.notes ? (
          <section className="space-y-4">
            <h2 className={sectionTitle}>Poznámky</h2>
            <p className="summary-note break-words whitespace-pre-line">{ceremony.notes}</p>
          </section>
        ) : null}
        {ceremony.documents.length ? (
          <section className="space-y-4">
            <h2 className={sectionTitle}>Podklady k obřadu</h2>
            <ul className="list-disc pl-5">
              {ceremony.documents.map((document) => (
                <li key={document.id} className="break-words">
                  {document.name}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </DetailSheet>
  )
}
